import React, { useState, useEffect } from 'react';
import { format, parse } from 'date-fns';
import type { FieldProps } from '../types';

/**
 * DateField Component
 *
 * Renders date picker with optional time.
 */
export const DateField: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const includeTime = properties?.includeTime || false;
  const dateFormat = properties?.dateFormat || 'MM/dd/yyyy';
  const timeFormat = properties?.timeFormat || '12h';

  // Convert stored value to input format
  const [localValue, setLocalValue] = useState(() => {
    if (!value) return '';
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      if (includeTime) {
        return format(date, "yyyy-MM-dd'T'HH:mm");
      }
      return format(date, 'yyyy-MM-dd');
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (!value) {
      setLocalValue('');
      return;
    }
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      if (includeTime) {
        setLocalValue(format(date, "yyyy-MM-dd'T'HH:mm"));
      } else {
        setLocalValue(format(date, 'yyyy-MM-dd'));
      }
    } catch {
      setLocalValue('');
    }
  }, [value, includeTime]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (newValue) {
      try {
        const date = new Date(newValue);
        // Store as ISO string
        onChange(date.toISOString());
      } catch {
        onChange(newValue);
      }
    } else {
      onChange(undefined);
    }
  };

  return (
    <div className="mediform-field mediform-date-field">
      {properties?.label && (
        <label htmlFor={element.id} className="mediform-field-label">
          {properties.label}
          {properties.required && <span className="mediform-required"> *</span>}
        </label>
      )}

      <div className="mediform-field-input">
        <input
          id={element.id}
          type={includeTime ? 'datetime-local' : 'date'}
          value={localValue}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          required={properties?.required}
          className={`mediform-input ${error ? 'mediform-input-error' : ''}`}
          style={{
            borderColor: error ? theme?.colors?.error : theme?.colors?.border,
          }}
        />
      </div>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}

      {properties?.dateFormat && (
        <div className="mediform-field-hint">
          Format: {dateFormat}
          {includeTime && ` (${timeFormat === '12h' ? '12-hour' : '24-hour'} time)`}
        </div>
      )}
    </div>
  );
};

export default DateField;
